/**
 * ImageToolbar (editor-image-tools) — ngwEditor 본문에서 이미지를 클릭(NodeSelection)하면 이미지
 * 위/아래에 뜨는 Floating 툴바. web·platform 공용 단일 소스(OMD-228). Figma floating action bar
 * 디자인(node 81205:452) 적용:
 *
 *   [↺ 왼쪽회전] [↻ 오른쪽회전] │ [⇋ 좌우반전] [⇵ 상하반전] │ [⤢ 크기 ▾]
 *                                                            └ 자동 ✓ / 작게(25%) / 중간(50%) / 크게(100%)
 *
 * 설계(design §4):
 *   - 어댑터(`editorImage`)의 `subscribeSelection` 으로 선택 상태를 구독하고, 선택된 이미지의
 *     `getBoundingClientRect()` 기준으로 portal(document.body, position: fixed) 위치를 계산.
 *   - 회전/반전은 내장 커맨드 wrapper(`rotate`/`flip`)를 호출 → 어댑터가 편집 중 즉시 반영.
 *     실제 픽셀 회전은 송신 시 `prepareForSend`(어댑터 canvas) 에서 수행.
 *   - 크기는 프리셋(자동/25/50/100) Dropdown → `setSize`. 현재 크기에 체크 표시.
 *   - 외부 http(s) 이미지처럼 canvas 픽셀화 불가한 경우(`rotatable === false`)는 회전/반전 버튼을
 *     비활성화하고 경고 툴팁을 표시(silent loss 방지). 네이티브 `disabled` 대신 `aria-disabled` 를
 *     사용해 비활성 상태에서도 툴팁이 뜨도록 한다.
 *   - 문구는 host(app)가 `labels` 로 주입한다(common-web props-only 규약 — `i18n` 직접 import 배제).
 *     web·platform 은 공통 번역키 `editor.image.*` 로 labels 를 채운다(OMD-228 키 통일).
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
// OmwTooltip(= PlainTooltip)는 자체 Provider 를 두지 않고 조상 Tooltip.Provider(OmwKitThemeHost)에
// 의존한다. 이 툴바는 document.body 로 portal 되어 여러 host(web app / platform compose·서명)에서
// 뜨는데, platform compose/서명 트리엔 OmwKitThemeHost 가 없어 Radix 가 throw 한다(OMD-228). 공용
// 컴포넌트가 host provider 설정에 의존하지 않도록 toolbar 를 자체 Tooltip.Provider 로 감싼다(self-
// contained). web 은 루트에 이미 provider 가 있어 중첩되지만 Radix 는 중첩 provider 를 허용한다.
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
// omw 심볼은 배럴(`../../omw`) 대신 개별 api 모듈에서 직접 가져온다 — 배럴 re-export 는 omw 전체
// (Drawer/Tree 등)를 끌어와 platform packages/ui strict tsc 그래프를 오염시킨다(OMD-228 design §5).
import { OmwTooltip } from '../../omw/api/OmwTooltip'
import { OmwDropdownMenu } from '../../omw/api/OmwDropdownMenu'
import { OmwIcon } from '../../omw/api/OmwIcon'
import type { OmwMenuItem, OmwMenuClickInfo } from '../../omw/api/OmwMenu'
import type { IEditorImage, SelectedImageState, ImageSizePreset } from '../IEditorImage'
import rotateLeftIcon from '../../../assets/images/editor/rotate-left.svg'
import rotateRightIcon from '../../../assets/images/editor/rotate-right.svg'
import flipVerticalIcon from '../../../assets/images/editor/flip-vertical.svg'
import flipHorizontalIcon from '../../../assets/images/editor/flip-horizontal.svg'
import imageScaleIcon from '../../../assets/images/editor/image-scale.svg'
import chevronDownIcon from '../../../assets/images/editor/chevron-down.svg'
import styles from './ImageToolbar.module.scss'

/**
 * ImageToolbar 문구 — common-web props-only 규약(i18n 미import)에 따라 host(app)가 주입.
 * web·platform 모두 번역키 `editor.image.*` 로 채운다(OMD-228 키 통일).
 */
export interface ImageToolbarLabels {
  rotateLeft: string
  rotateRight: string
  flipH: string
  flipV: string
  /** 외부 이미지 등 회전 불가 시 비활성 버튼 툴팁. */
  rotateUnavailable: string
  sizeLabel: string
  sizeAuto: string
  sizeSmall: string
  sizeMedium: string
  sizeLarge: string
}

/**
 * 통일 번역키(`editor.image.*`)로 `ImageToolbarLabels` 를 구성한다(OMD-228). 키 문자열을 common-web
 * 단일 소스에 두어 web·platform 의 키 divergence 를 원천 차단한다. host(app)는 자신의 i18n `t`
 * (dotted key → 문구)만 주입한다 — common-web 은 i18n 을 직접 import 하지 않는다.
 */
export function buildImageToolbarLabels(t: (key: string) => string): ImageToolbarLabels {
  return {
    rotateLeft: t('editor.image.rotateLeft'),
    rotateRight: t('editor.image.rotateRight'),
    flipH: t('editor.image.flipH'),
    flipV: t('editor.image.flipV'),
    rotateUnavailable: t('editor.image.rotateUnavailable'),
    sizeLabel: t('editor.image.size.label'),
    sizeAuto: t('editor.image.size.auto'),
    sizeSmall: t('editor.image.size.small'),
    sizeMedium: t('editor.image.size.medium'),
    sizeLarge: t('editor.image.size.large'),
  }
}

interface ImageToolbarProps {
  /** 어댑터 image 표면 (NgwEditor: 실제 동작 / TinyMCE: null 구독) */
  editorImage: IEditorImage
  /** ngwEditor 모드에서만 활성 */
  enabled: boolean
  /** 문구 (host wrapper 가 i18n 으로 채워 주입). */
  labels: ImageToolbarLabels
}

/** 툴바 전체 높이(px) — 버튼 32 + 상하 padding 4×2 + border 1×2 (Figma 81205:452). */
const BAR_HEIGHT = 42
/** 툴바 전체 폭(px) — 좌우 clamp 용. padding 16 + 버튼 32×4 + 구분선 16×2 + 크기버튼 48 + border 2. */
const BAR_WIDTH = 226
const GAP = 8
/** 툴팁 hover 표시 지연(ms) — OmwKitThemeHost 의 TOOLTIP_DELAY_MS 와 동일(antd mouseEnterDelay 0.1s 파리티). */
const TOOLTIP_DELAY_MS = 100

interface BarPosition {
  top: number
  left: number
}

/**
 * 선택 이미지가 속한 스크롤 컨테이너(에디터 스크롤 영역)의 가시 영역 상/하단(viewport 좌표)을 구한다.
 * 스크롤 가능한 조상이 없으면 뷰포트(0 ~ innerHeight)를 반환. 이미지의 상/하단이 이 영역을 벗어나면
 * "잘림"으로 판정해 툴바 위치를 결정한다.
 */
function getVisibleBounds(el: HTMLElement): { top: number; bottom: number } {
  let node = el.parentElement
  while (node != null && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node)
    if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      const rect = node.getBoundingClientRect()
      return { top: rect.top, bottom: rect.bottom }
    }
    node = node.parentElement
  }
  return { top: 0, bottom: window.innerHeight }
}

interface ToolButtonProps {
  title: string
  iconSrc: string
  disabled: boolean
  onClick: () => void
}

/** 32×32 원형 아이콘 버튼(아이콘 16×16). aria-disabled 로 비활성 시에도 툴팁 표시. */
function ToolButton({ title, iconSrc, disabled, onClick }: ToolButtonProps): JSX.Element {
  return (
    <OmwTooltip title={title}>
      <button
        type="button"
        className={styles.iconButton}
        aria-disabled={disabled}
        onClick={disabled ? undefined : onClick}
      >
        <img src={iconSrc} alt="" width={16} height={16} />
      </button>
    </OmwTooltip>
  )
}

export function ImageToolbar({
  editorImage,
  enabled,
  labels,
}: ImageToolbarProps): JSX.Element | null {
  const [selected, setSelected] = useState<SelectedImageState | null>(null)
  const [position, setPosition] = useState<BarPosition | null>(null)
  // 사이즈 드롭다운을 툴바 루트(data-ngw-portal 보유) 내부로 포털시키기 위한 ref. platform 서명
  // 모달에서 드롭다운 content 가 이 마커의 자손이 되어 방어막 3종(closest('[data-ngw-portal]'))의
  // 대상이 된다 — omw 소스 수정 없이 modal 내 클릭·dismiss·focus-trap 을 보존한다(design §4.3 옵션1 변형).
  const toolbarRef = useRef<HTMLDivElement>(null)

  // 선택 상태 구독.
  useEffect(() => {
    if (!enabled) {
      setSelected(null)
      return
    }
    setSelected(editorImage.getSelectedImage())
    return editorImage.subscribeSelection(setSelected)
  }, [editorImage, enabled])

  // 위치 계산 + 스크롤/리사이즈 재계산.
  useLayoutEffect(() => {
    if (selected == null) {
      setPosition(null)
      return
    }
    const compute = (): void => {
      const rect = selected.dom.getBoundingClientRect()
      const vis = getVisibleBounds(selected.dom)
      const topClipped = rect.top < vis.top // 이미지 윗쪽이 가시 영역 위로 잘림
      const bottomClipped = rect.bottom > vis.bottom // 이미지 아랫쪽이 가시 영역 아래로 잘림
      const above = rect.top - BAR_HEIGHT - GAP
      const below = rect.bottom + GAP

      let top: number
      if (topClipped && bottomClipped) {
        // 위·아래 모두 잘림 → 보이는 부분 상단 위(가시 영역 안쪽)에 표시.
        top = vis.top + GAP
      } else if (topClipped) {
        // 윗쪽이 안 보임 → 이미지 하단에. 단 하단도 가시 영역을 벗어나면 보이는 부분 위로.
        top = below + BAR_HEIGHT <= vis.bottom ? below : vis.top + GAP
      } else if (bottomClipped) {
        // 아랫쪽이 안 보임 → 이미지 상단에. 단 위 공간이 부족하면 보이는 부분 위로.
        top = above >= vis.top ? above : vis.top + GAP
      } else {
        // 전체가 보임 → 상단 우선, 위 공간 없으면 하단.
        top = above >= vis.top ? above : below
      }

      const maxLeft = Math.max(0, window.innerWidth - BAR_WIDTH)
      const left = Math.min(Math.max(0, rect.left), maxLeft)
      setPosition({ top, left })
    }
    compute()
    window.addEventListener('scroll', compute, true)
    window.addEventListener('resize', compute)
    return () => {
      window.removeEventListener('scroll', compute, true)
      window.removeEventListener('resize', compute)
    }
  }, [selected])

  // 에디터/플로팅 메뉴 바깥 클릭 시 selection 해제는 어댑터(NgwEditorLifecycle.attachOutsideSelectionClear)
  // 가 담당한다 — selection 이 풀리면 imageSelection plugin → setSelectedImage(null) 로 이 툴바가
  // 자동으로 닫히므로, 뷰에서 별도의 강제 close 리스너를 두지 않는다(OMD-228).

  const onRotateLeft = useCallback(() => editorImage.rotate('left'), [editorImage])
  const onRotateRight = useCallback(() => editorImage.rotate('right'), [editorImage])
  const onFlipV = useCallback(() => editorImage.flip('vertical'), [editorImage])
  const onFlipH = useCallback(() => editorImage.flip('horizontal'), [editorImage])
  const onSelectSize = useCallback(
    (info: OmwMenuClickInfo) => {
      const size = Number.parseInt(info.key, 10) as ImageSizePreset
      editorImage.setSize(size)
    },
    [editorImage],
  )

  if (selected == null || position == null) {
    return null
  }

  const rotatable = selected.rotatable
  const disabledTip = rotatable ? '' : labels.rotateUnavailable
  const currentSize = selected.size

  // 현재 크기에 체크 표시(캡처의 자동 ✓ 대응).
  const sizeLabel = (text: string, key: ImageSizePreset): JSX.Element => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 24,
        minWidth: 96,
      }}
    >
      <span>{text}</span>
      {currentSize === key ? <OmwIcon name="check" /> : null}
    </div>
  )
  const sizeItems: OmwMenuItem[] = [
    { key: '0', label: sizeLabel(labels.sizeAuto, 0) },
    { key: '1', label: sizeLabel(labels.sizeSmall, 1) },
    { key: '2', label: sizeLabel(labels.sizeMedium, 2) },
    { key: '3', label: sizeLabel(labels.sizeLarge, 3) },
  ]

  // mousedown 단계에서 selection 해제 방지(에디터 NodeSelection 유지).
  const preventBlur = (e: ReactMouseEvent): void => {
    e.preventDefault()
  }

  const toolbar = (
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY_MS}>
      <div
        ref={toolbarRef}
        role="toolbar"
        // 서명 편집은 platform 의 Radix modal(OmDialog) 안에서 열린다 — 열릴 때 body 에
        // pointer-events:none 이 걸리고 focus-trap/dismissable-layer 가 이 body 포털을 "바깥"으로
        // 취급한다. ngwEditor 포털과 동일하게 data-ngw-portal 로 표시하면 platform 방어막 3종
        // (pointer-events 복구 · focus-trap 우회 · dismiss 가드)의 대상이 된다. 비모달(compose·web)
        // 에서는 모두 no-op 이라 무해하다.
        data-ngw-portal=""
        // `om-light-island` — platform(desktop) 다크모드 표식. 이 툴바는 body 로 포털되어
        // 편집기 wrapper(ComposeForm·SignatureRichTextEditor)의 `om-light-island` 섬 밖으로
        // 나가므로, 다크 테마(`:root.dark`)에서 아이콘 `<img src=data:image/svg>` 가 반전 필터에
        // 걸려 흰 툴바 위에서 사라진다. 섬 표식을 직접 달아 강제 light(토큰·color-scheme·아이콘
        // 반전 제외)로 둬 편집기와 일관되게 한다. web 은 해당 CSS 가 없어 no-op(무해).
        onMouseDown={preventBlur}
        className={`${styles.toolbar} om-light-island`}
        style={{ top: position.top, left: position.left }}
      >
        <ToolButton
          title={rotatable ? labels.rotateLeft : disabledTip}
          iconSrc={rotateLeftIcon}
          disabled={!rotatable}
          onClick={onRotateLeft}
        />
        <ToolButton
          title={rotatable ? labels.rotateRight : disabledTip}
          iconSrc={rotateRightIcon}
          disabled={!rotatable}
          onClick={onRotateRight}
        />
        <div className={styles.separator} />
        <ToolButton
          title={rotatable ? labels.flipH : disabledTip}
          iconSrc={flipHorizontalIcon}
          disabled={!rotatable}
          onClick={onFlipH}
        />
        <ToolButton
          title={rotatable ? labels.flipV : disabledTip}
          iconSrc={flipVerticalIcon}
          disabled={!rotatable}
          onClick={onFlipV}
        />
        <div className={styles.separator} />
        <OmwDropdownMenu
          menu={{ items: sizeItems, onClick: onSelectSize }}
          trigger={['click']}
          getPopupContainer={() => toolbarRef.current ?? document.body}
        >
          <OmwTooltip title={labels.sizeLabel}>
            <button type="button" className={styles.scaleButton}>
              <img src={imageScaleIcon} alt="" width={16} height={16} />
              <img src={chevronDownIcon} alt="" width={16} height={16} />
            </button>
          </OmwTooltip>
        </OmwDropdownMenu>
      </div>
    </TooltipPrimitive.Provider>
  )

  return createPortal(toolbar, document.body)
}
